import { QUERY_KEYS } from '@avanta/shared/constants/common';
import { useDebounce } from '@avanta/shared/hooks/useDebounce';
import { useServerPaginatedTable } from '@avanta/shared/hooks/useServerPaginatedTable';
import { type User } from '@avanta/shared/types/users';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { type ColumnDef, type Table, type Updater } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { EnterprisesService } from 'services/enterprises';
import { useEnterpriseId } from 'stores/user';
import { type AppointmentWithDetails } from 'types/appointments';
import { matchesSessionFilter, type PatientSessionFilter } from '../utils';

const DEFAULT_PAGE_SIZE = 8;

export const useFetchInsuredUsers = (
  columns: ColumnDef<User>[],
  appointmentsByPatientId: Map<string, AppointmentWithDetails>,
  pageSize: number = DEFAULT_PAGE_SIZE
): {
  table: Table<User>;
  query: UseQueryResult<User[], Error>;
  totalCount: number;
  employeeTotalCount: number;
  isLoading: boolean;
  selectedStatusFilter: PatientSessionFilter | undefined;
  setStatusFilter: (value: PatientSessionFilter | undefined) => void;
} => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize,
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<
    PatientSessionFilter | undefined
  >();
  const enterpriseId = useEnterpriseId();
  const debouncedSearch = useDebounce(globalFilter, 500).trim();
  const hasSearch = Boolean(debouncedSearch);
  // Search/session filter are applied over a full result set → client paging.
  const needsClientPaging = hasSearch || Boolean(selectedStatusFilter);

  const resetPagination = (): void => {
    setPagination(previousPagination =>
      previousPagination.pageIndex === 0
        ? previousPagination
        : { ...previousPagination, pageIndex: 0 }
    );
  };

  const handleGlobalFilterChange = (updater: Updater<string>): void => {
    setGlobalFilter(updater);
    resetPagination();
  };

  const setStatusFilter = (value: PatientSessionFilter | undefined): void => {
    setSelectedStatusFilter(value);
    resetPagination();
  };

  const totalCountQuery = useQuery({
    queryKey: [QUERY_KEYS.FETCH_INSURED_USERS_COUNT, enterpriseId],
    queryFn: () => EnterprisesService.getCountEmployeesByEnterprise(enterpriseId),
    select: response => response.data.count,
    enabled: !!enterpriseId,
  });
  const employeeTotalCount = totalCountQuery.data ?? 0;

  const query = useQuery({
    queryKey: [
      QUERY_KEYS.FETCH_INSURED_USERS,
      enterpriseId,
      needsClientPaging ? 0 : pagination.pageIndex,
      needsClientPaging ? Math.max(employeeTotalCount, pageSize) : pagination.pageSize,
      debouncedSearch,
    ],
    queryFn: async () => {
      const { data } = await EnterprisesService.getEmployeesByEnterprise({
        enterpriseId,
        offset: needsClientPaging ? 0 : pagination.pageIndex,
        limit: needsClientPaging ? Math.max(employeeTotalCount, pageSize) : pagination.pageSize,
        search: debouncedSearch || undefined,
      });
      return data;
    },
    placeholderData: previousData => previousData,
    enabled: !!enterpriseId && (hasSearch || !selectedStatusFilter || totalCountQuery.isSuccess),
  });

  const filteredRows = useMemo(() => {
    const rows = query.data ?? [];

    return rows.filter(patient =>
      matchesSessionFilter(patient.id, appointmentsByPatientId, selectedStatusFilter)
    );
  }, [appointmentsByPatientId, query.data, selectedStatusFilter]);

  const visibleRows = needsClientPaging
    ? filteredRows.slice(
        pagination.pageIndex * pagination.pageSize,
        pagination.pageIndex * pagination.pageSize + pagination.pageSize
      )
    : filteredRows;

  const totalCount = needsClientPaging ? filteredRows.length : employeeTotalCount;

  const table = useServerPaginatedTable<User>({
    columns,
    data: visibleRows,
    totalCount,
    pagination,
    onPaginationChange: setPagination,
    globalFilter,
    onGlobalFilterChange: handleGlobalFilterChange,
  });

  return {
    table,
    query,
    totalCount,
    employeeTotalCount,
    isLoading: totalCountQuery.isLoading || query.isLoading,
    selectedStatusFilter,
    setStatusFilter,
  };
};
